<template>
  <admin-template>
    <template #header>
      <div class="page-header">
        <h1 class="page-title">Nouvelle société</h1>
        <p class="page-subtitle">Initialisation d'un espace Termoflow</p>
      </div>
    </template>

    <!-- SUCCESS OVERLAY -->
    <transition name="success-screen">
      <div v-if="showSuccess"
        class="fixed inset-0 z-50 flex items-center justify-center"
        style="background:rgba(15,23,42,0.75); backdrop-filter:blur(8px)">
        <canvas ref="confettiCanvas" class="absolute inset-0 pointer-events-none" style="width:100%;height:100%"></canvas>
        <div class="relative bg-white rounded-3xl overflow-hidden text-center success-card"
          style="width:480px; max-width:calc(100vw - 2rem); box-shadow:0 40px 80px rgba(0,0,0,0.35)">
          <div class="h-2 w-full" style="background:linear-gradient(90deg,#f97316,#ea580c,#dc2626)"></div>
          <div class="px-10 py-10">
            <div class="relative w-24 h-24 mx-auto mb-6">
              <div class="absolute inset-0 rounded-full" style="background:rgba(234,88,12,0.12); animation:ping 1.4s cubic-bezier(0,0,0.2,1) infinite"></div>
              <div class="absolute inset-2 rounded-full" style="background:rgba(234,88,12,0.08); animation:ping 1.4s cubic-bezier(0,0,0.2,1) infinite; animation-delay:0.4s"></div>
              <div class="absolute inset-0 rounded-full flex items-center justify-center"
                style="background:linear-gradient(135deg,#f97316,#ea580c); box-shadow:0 16px 40px rgba(234,88,12,0.5)">
                <i class="fas fa-rocket text-white" style="font-size:36px; transform:rotate(-30deg)"></i>
              </div>
            </div>
            <h2 class="text-2xl font-black text-slate-900 mb-2">C'est parti&nbsp;! 🎉</h2>
            <p class="text-slate-500 text-sm leading-relaxed mb-1">
              La société <strong class="text-slate-800">{{ createdName }}</strong> a été créée avec succès.
            </p>
            <p class="text-slate-400 text-xs mb-8">L'administrateur recevra son lien de connexion par email.</p>
            <div class="grid grid-cols-3 gap-3 mb-8">
              <div class="rounded-2xl px-3 py-3" style="background:#fff7ed; border:1px solid #fed7aa">
                <div class="text-xl font-black" style="color:#ea580c">{{ createdActivites }}</div>
                <div class="text-xs text-orange-400 font-medium mt-0.5">Activit{{ createdActivites > 1 ? "és" : "é" }}</div>
              </div>
              <div class="rounded-2xl px-3 py-3" style="background:#f0fdf4; border:1px solid #bbf7d0">
                <div class="text-xl font-black text-emerald-600">{{ createdFormule }}</div>
                <div class="text-xs text-emerald-400 font-medium mt-0.5">Formule</div>
              </div>
              <div class="rounded-2xl px-3 py-3" style="background:#eff6ff; border:1px solid #bfdbfe">
                <div class="text-xl font-black text-blue-600">1</div>
                <div class="text-xs text-blue-400 font-medium mt-0.5">Admin</div>
              </div>
            </div>
            <div class="flex gap-3">
              <button @click="resetAndClose"
                class="flex-1 h-11 rounded-xl border-2 border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Nouvelle société
              </button>
              <button @click="resetAndClose"
                class="flex-1 h-11 rounded-xl text-white text-sm font-bold"
                style="background:linear-gradient(135deg,#f97316,#ea580c); box-shadow:0 4px 16px rgba(234,88,12,0.4)">
                <i class="fas fa-arrow-right mr-2 text-xs"></i>Terminer
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="!showSuccess" class="flex gap-10 items-start max-w-5xl mx-auto pb-16">

      <!-- ══ SIDEBAR STEPPER ══ -->
      <aside class="w-56 flex-shrink-0 sticky top-8 pt-1">
        <div class="mb-8">
          <p class="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Progression</p>
          <p class="text-sm text-slate-500">Quelques étapes pour tout configurer.</p>
        </div>

        <nav>
          <div v-for="(s, i) in steps" :key="s.key" class="flex gap-3">
            <div class="flex flex-col items-center flex-shrink-0" style="width: 32px">
              <button
                @click="i < stepIndex ? goTo(i) : null"
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 z-10 flex-shrink-0"
                :class="i < stepIndex
                  ? 'bg-orange-600 border-orange-600 text-white cursor-pointer hover:scale-105'
                  : i === stepIndex
                    ? 'bg-white border-orange-500 text-orange-600 cursor-default shadow-md shadow-orange-100'
                    : 'bg-white border-slate-200 text-slate-300 cursor-default'"
              >
                <i v-if="i < stepIndex" class="fas fa-check" style="font-size:10px"></i>
                <span v-else style="font-size:11px">{{ i + 1 }}</span>
              </button>

              <div v-if="i < steps.length - 1"
                class="flex-1 rounded-full overflow-hidden my-1.5"
                style="width:2px; min-height:32px; background:#e2e8f0">
                <div class="w-full transition-all duration-500 rounded-full"
                  style="background:#ea580c"
                  :style="{ height: i < stepIndex ? '100%' : '0%' }">
                </div>
              </div>
            </div>

            <div class="pb-8 pt-0.5 min-w-0">
              <p class="text-sm font-semibold leading-tight transition-colors"
                :class="i === stepIndex ? 'text-orange-600' : i < stepIndex ? 'text-slate-700' : 'text-slate-300'">
                {{ s.label }}
              </p>
              <p class="text-xs mt-0.5 transition-colors"
                :class="i === stepIndex ? 'text-slate-500' : 'text-slate-300'">
                {{ s.sublabel }}
              </p>
            </div>
          </div>
        </nav>
      </aside>

      <!-- ══ MAIN CARD ══ -->
      <div class="flex-1 min-w-0">
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

          <transition :css="false" mode="out-in"
            @before-enter="tBeforeEnter" @enter="tEnter" @leave="tLeave">
            <div :key="currentStep.key" class="px-10 pt-10 pb-6">


              <!-- ─── STEP 1 : SIRET ─── -->
              <div v-if="currentStep.key === 'siret'" class="py-6">
                <div class="text-center mb-10">
                  <div class="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg"
                    style="background:#ea580c; box-shadow:0 12px 28px rgba(234,88,12,0.35)">
                    <i class="fas fa-building-shield text-white text-2xl"></i>
                  </div>
                  <h3 class="text-2xl font-bold text-slate-900 mb-2">Commençons par le SIRET</h3>
                  <p class="text-sm text-slate-500 max-w-xs mx-auto leading-relaxed">
                    Toutes les informations de la société seront pré-remplies automatiquement à partir du SIRET. Vous pourrez tout modifier ensuite.
                  </p>
                </div>

                <div class="max-w-sm mx-auto space-y-4">
                  <div class="relative">
                    <input
                      v-model="form.societe.siret"
                      type="text"
                      placeholder="14 chiffres"
                      @input="onSiretInput"
                      maxlength="14"
                      autofocus
                      class="w-full border-2 rounded-2xl px-5 py-4 text-xl outline-none transition-all duration-200 font-mono tracking-widest text-center"
                      :class="siretFieldClass"
                    />
                    <div class="absolute right-4 top-1/2 -translate-y-1/2">
                      <i v-if="pappersLoading" class="fas fa-spinner fa-spin text-orange-400 text-lg"></i>
                      <i v-else-if="pappersFilled" class="fas fa-circle-check text-emerald-500 text-lg"></i>
                      <i v-else-if="touched.siret && errors.siret" class="fas fa-circle-exclamation text-red-400 text-lg"></i>
                    </div>
                  </div>

                  <transition name="fade">
                    <div v-if="pappersLoading"
                      class="flex items-center justify-center gap-2 text-sm text-orange-600 font-medium">
                      <i class="fas fa-spinner fa-spin"></i>
                      <span>Recherche dans les registres…</span>
                    </div>
                  </transition>
                  <transition name="fade">
                    <div v-if="pappersFilled && !pappersLoading"
                      class="flex items-center justify-center gap-2 text-sm text-emerald-600 font-semibold">
                      <i class="fas fa-wand-magic-sparkles"></i>
                      <span>Société trouvée — passage à l'étape suivante…</span>
                    </div>
                  </transition>
                  <transition name="fade">
                    <div v-if="pappersError && !pappersLoading"
                      class="flex items-center justify-center gap-2 text-sm text-red-600 font-medium">
                      <i class="fas fa-triangle-exclamation"></i>
                      <span>{{ pappersError }}</span>
                    </div>
                  </transition>
                  <transition name="fade">
                    <p v-if="touched.siret && errors.siret" class="text-center text-xs font-medium" style="color:#ef4444">
                      {{ errors.siret }}
                    </p>
                  </transition>
                </div>
              </div>


              <!-- ─── STEP 2 : SOCIÉTÉ ─── -->
              <div v-else-if="currentStep.key === 'societe'" class="space-y-8">

                <!-- Bandeau autofill -->
                <div class="flex items-center gap-2.5 px-4 py-3 rounded-xl text-xs font-semibold"
                  style="background:#fff7ed; border:1px solid #fed7aa; color:#c2410c">
                  <i class="fas fa-wand-magic-sparkles flex-shrink-0"></i>
                  <span>Données récupérées depuis le SIRET — vérifiez et complétez les champs manquants</span>
                </div>

                <!-- ── Section 1 : Identité ── -->
                <div>
                  <div class="flex items-center gap-2 mb-4">
                    <div class="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style="background:#ea580c">
                      <i class="fas fa-building text-white" style="font-size:10px"></i>
                    </div>
                    <h4 class="text-sm font-bold text-slate-700 uppercase tracking-wide">Identité de la société</h4>
                  </div>

                  <div class="space-y-4 pl-8">
                    <!-- Logo + Nom -->
                    <div class="flex gap-4 items-start">
                      <div class="flex-shrink-0">
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">Logo</label>
                        <input-file key-name="logo" v-model="logoUrl" :preview="false" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Nom de la société <span style="color:#ea580c">*</span>
                          <span v-if="autoFilled.nom" class="ml-1.5 font-normal normal-case px-1.5 py-0.5 rounded text-orange-600" style="letter-spacing:0; font-size:9px; background:#fff7ed">
                            <i class="fas fa-magic mr-1" style="font-size:8px"></i>auto
                          </span>
                        </label>
                        <input v-model="form.societe.nom" type="text" placeholder="Ex : Chauffage Dupont SARL"
                          @blur="touch('nom')" @input="autoFilled.nom = false"
                          class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200"
                          :class="autoFilled.nom ? 'border-orange-200 bg-orange-50 focus:border-orange-400 focus:bg-white text-slate-900' : fieldClass('nom', form.societe.nom)"
                        />
                        <p v-if="touched.nom && errors.nom" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">
                          <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.nom }}
                        </p>
                        <p v-if="form.societe.forme_juridique" class="mt-1 ml-1 text-xs text-slate-400">
                          <i class="fas fa-tag mr-1"></i>{{ form.societe.forme_juridique }}
                        </p>
                      </div>
                    </div>

                    <!-- Responsable -->
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Prénom responsable <span style="color:#ea580c">*</span>
                          <span v-if="autoFilled.responsable_prenom" class="ml-1.5 font-normal normal-case px-1.5 py-0.5 rounded text-orange-600" style="letter-spacing:0; font-size:9px; background:#fff7ed">
                            <i class="fas fa-magic mr-0.5" style="font-size:8px"></i>auto
                          </span>
                        </label>
                        <input v-model="form.societe.responsable_prenom" type="text" placeholder="Jean"
                          @blur="touch('responsable_prenom')" @input="autoFilled.responsable_prenom = false; syncAdminFromResponsable()"
                          class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 text-slate-900"
                          :class="autoFilled.responsable_prenom ? 'border-orange-200 bg-orange-50 focus:border-orange-400 focus:bg-white' : fieldClass('responsable_prenom', form.societe.responsable_prenom)"
                        />
                        <p v-if="touched.responsable_prenom && errors.responsable_prenom" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.responsable_prenom }}</p>
                      </div>
                      <div>
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Nom responsable <span style="color:#ea580c">*</span>
                          <span v-if="autoFilled.responsable_nom" class="ml-1.5 font-normal normal-case px-1.5 py-0.5 rounded text-orange-600" style="letter-spacing:0; font-size:9px; background:#fff7ed">
                            <i class="fas fa-magic mr-0.5" style="font-size:8px"></i>auto
                          </span>
                        </label>
                        <input v-model="form.societe.responsable_nom" type="text" placeholder="Dupont"
                          @blur="touch('responsable_nom')" @input="autoFilled.responsable_nom = false; syncAdminFromResponsable()"
                          class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 text-slate-900"
                          :class="autoFilled.responsable_nom ? 'border-orange-200 bg-orange-50 focus:border-orange-400 focus:bg-white' : fieldClass('responsable_nom', form.societe.responsable_nom)"
                        />
                        <p v-if="touched.responsable_nom && errors.responsable_nom" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.responsable_nom }}</p>
                      </div>
                    </div>

                    <!-- N° Capacité -->
                    <div class="max-w-xs">
                      <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                        N° de capacité
                        <span class="font-normal normal-case ml-1" style="letter-spacing:0; color:#cbd5e1; font-size:9px">fluides frigorigènes — optionnel</span>
                      </label>
                      <input v-model="form.societe.num_capacite" type="text" placeholder="FR-XXX-XXXXXX"
                        class="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 bg-slate-50 focus:bg-white focus:border-orange-400 text-slate-900 font-mono" />
                    </div>
                  </div>
                </div>

                <!-- Divider -->
                <div class="border-t border-slate-100"></div>

                <!-- ── Section 2 : Localisation ── -->
                <div>
                  <div class="flex items-center gap-2 mb-4">
                    <div class="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style="background:#3b82f6">
                      <i class="fas fa-map-marker-alt text-white" style="font-size:10px"></i>
                    </div>
                    <h4 class="text-sm font-bold text-slate-700 uppercase tracking-wide">Adresse</h4>
                  </div>

                  <div class="space-y-4 pl-8">
                    <div>
                      <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                        Adresse <span style="color:#ea580c">*</span>
                        <span v-if="autoFilled.adresse" class="ml-1.5 font-normal normal-case px-1.5 py-0.5 rounded text-orange-600" style="letter-spacing:0; font-size:9px; background:#fff7ed">
                          <i class="fas fa-magic mr-0.5" style="font-size:8px"></i>auto
                        </span>
                      </label>
                      <div class="relative">
                        <i class="fas fa-map-marker-alt absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" style="font-size:12px"></i>
                        <input v-model="form.societe.adresse" type="text"
                          placeholder="12 rue des Artisans…"
                          @input="onAdresseInput(); autoFilled.adresse = false"
                          @blur="touch('adresse'); closeAdresse()"
                          class="w-full border-2 rounded-xl py-3 text-sm outline-none transition-all duration-200 text-slate-900"
                          style="padding-left:2.5rem; padding-right:2.5rem"
                          :class="autoFilled.adresse ? 'border-orange-200 bg-orange-50 focus:border-orange-400 focus:bg-white' : fieldClass('adresse', form.societe.adresse)"
                        />
                        <i v-if="adresseLoading" class="fas fa-spinner fa-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" style="font-size:11px"></i>

                        <transition name="dropdown">
                          <div v-if="adresseSuggestions.length"
                            class="absolute z-30 left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 overflow-hidden"
                            style="box-shadow:0 20px 40px rgba(0,0,0,0.10); top:100%">
                            <div
                              v-for="(s, si) in adresseSuggestions" :key="si"
                              @mousedown.prevent="adresseSelect(s)"
                              class="flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-slate-50 last:border-0 hover:bg-orange-50 transition-colors group"
                            >
                              <i class="fas fa-location-dot text-orange-400 group-hover:text-orange-600 flex-shrink-0" style="font-size:11px"></i>
                              <span class="text-sm text-slate-700">{{ s.label }}</span>
                            </div>
                          </div>
                        </transition>
                      </div>
                      <p v-if="touched.adresse && errors.adresse" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">
                        <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.adresse }}
                      </p>
                    </div>

                    <div class="grid gap-3" style="grid-template-columns:130px 1fr">
                      <div>
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Code postal
                          <span v-if="autoFilled.code_postal" class="ml-1 font-normal normal-case px-1.5 py-0.5 rounded text-orange-600" style="letter-spacing:0; font-size:9px; background:#fff7ed">
                            <i class="fas fa-magic" style="font-size:8px"></i>
                          </span>
                        </label>
                        <input v-model="form.societe.code_postal" type="text" placeholder="69001"
                          @input="autoFilled.code_postal = false"
                          class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 font-mono text-slate-900"
                          :class="autoFilled.code_postal ? 'border-orange-200 bg-orange-50 focus:border-orange-400 focus:bg-white' : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-orange-400'"
                        />
                      </div>
                      <div>
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Ville <span style="color:#ea580c">*</span>
                          <span v-if="autoFilled.ville" class="ml-1 font-normal normal-case px-1.5 py-0.5 rounded text-orange-600" style="letter-spacing:0; font-size:9px; background:#fff7ed">
                            <i class="fas fa-magic" style="font-size:8px"></i>
                          </span>
                        </label>
                        <input v-model="form.societe.ville" type="text" placeholder="Lyon"
                          @blur="touch('ville')" @input="autoFilled.ville = false"
                          class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 text-slate-900"
                          :class="autoFilled.ville ? 'border-orange-200 bg-orange-50 focus:border-orange-400 focus:bg-white' : fieldClass('ville', form.societe.ville)"
                        />
                        <p v-if="touched.ville && errors.ville" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.ville }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Divider -->
                <div class="border-t border-slate-100"></div>

                <!-- ── Section 3 : Contact ── -->
                <div>
                  <div class="flex items-center gap-2 mb-4">
                    <div class="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style="background:#10b981">
                      <i class="fas fa-address-book text-white" style="font-size:10px"></i>
                    </div>
                    <h4 class="text-sm font-bold text-slate-700 uppercase tracking-wide">Contact</h4>
                  </div>

                  <div class="space-y-4 pl-8">
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Téléphone <span style="color:#ea580c">*</span>
                        </label>
                        <div class="relative">
                          <i class="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" style="font-size:11px"></i>
                          <input v-model="form.societe.tel" type="tel" placeholder="04 72 00 00 00"
                            @blur="touch('tel')" @input="formatTel"
                            class="w-full border-2 rounded-xl py-3 text-sm outline-none transition-all duration-200 text-slate-900"
                            style="padding-left:2.5rem"
                            :class="fieldClass('tel', form.societe.tel)"
                          />
                        </div>
                        <p v-if="touched.tel && errors.tel" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.tel }}</p>
                      </div>
                      <div>
                        <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                          Email société <span style="color:#ea580c">*</span>
                        </label>
                        <div class="relative">
                          <i class="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" style="font-size:11px"></i>
                          <input v-model="form.societe.mail" type="email" placeholder="contact@societe.fr"
                            @blur="touch('mail'); syncAdminMailFromSociete()"
                            class="w-full border-2 rounded-xl py-3 text-sm outline-none transition-all duration-200 text-slate-900"
                            style="padding-left:2.5rem"
                            :class="fieldClass('mail', form.societe.mail)"
                          />
                        </div>
                        <p v-if="touched.mail && errors.mail" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.mail }}</p>
                      </div>
                    </div>

                    <div>
                      <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                        Lien Google Avis
                        <span class="font-normal normal-case ml-1" style="letter-spacing:0; color:#cbd5e1; font-size:9px">optionnel</span>
                      </label>
                      <div class="relative">
                        <i class="fab fa-google absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 text-sm"></i>
                        <input v-model="form.societe.url_google_avis" type="url" placeholder="https://g.page/…"
                          class="w-full border-2 border-slate-200 rounded-xl py-3 text-sm outline-none transition-all duration-200 bg-slate-50 focus:bg-white focus:border-orange-400 text-slate-900"
                          style="padding-left:2.5rem" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              <!-- ─── STEP 3 : HORAIRES ─── -->
              <div v-else-if="currentStep.key === 'horaires'" class="space-y-7">
                <div>
                  <label class="block mb-3" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                    Jours travaillés <span style="color:#ea580c">*</span>
                  </label>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="jour in jours" :key="jour.key"
                      @click="toggleJour(jour.key); touch('open_days')"
                      class="flex flex-col items-center px-3.5 py-2.5 rounded-xl border-2 transition-all duration-200 select-none cursor-pointer font-medium"
                      :class="form.societe.horaires.open_days.includes(jour.key)
                        ? 'border-orange-500 text-white'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-orange-300 hover:text-orange-500'"
                      :style="form.societe.horaires.open_days.includes(jour.key)
                        ? 'background:#ea580c; box-shadow:0 4px 12px rgba(234,88,12,0.30)'
                        : ''"
                    >
                      <span class="text-xs font-bold">{{ jour.short }}</span>
                      <span class="opacity-70" style="font-size:9px; margin-top:2px">{{ jour.full }}</span>
                    </button>
                  </div>
                  <p v-if="touched.open_days && form.societe.horaires.open_days.length === 0"
                    class="mt-2 ml-1 text-xs font-medium" style="color:#ef4444">
                    <i class="fas fa-circle-exclamation mr-1"></i>Sélectionnez au moins un jour.
                  </p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                      <i class="fas fa-sun text-amber-400 mr-1.5"></i>Ouverture <span style="color:#ea580c">*</span>
                    </label>
                    <input v-model="form.societe.horaires.heure_ouverture" type="time"
                      @change="touch('heure_ouverture')"
                      class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 font-mono"
                      :class="fieldClass('heure_ouverture', form.societe.horaires.heure_ouverture)"
                    />
                  </div>
                  <div>
                    <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                      <i class="fas fa-moon text-indigo-400 mr-1.5"></i>Fermeture <span style="color:#ea580c">*</span>
                    </label>
                    <input v-model="form.societe.horaires.heure_fermeture" type="time"
                      @change="touch('heure_fermeture')"
                      class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 font-mono"
                      :class="fieldClass('heure_fermeture', form.societe.horaires.heure_fermeture)"
                    />
                    <p v-if="errors.heure_fermeture" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">
                      <i class="fas fa-circle-exclamation mr-1"></i>{{ errors.heure_fermeture }}
                    </p>
                  </div>
                </div>

                <transition name="fade">
                  <div v-if="form.societe.horaires.open_days.length && form.societe.horaires.heure_ouverture && form.societe.horaires.heure_fermeture && !errors.heure_fermeture"
                    class="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium"
                    style="background:#fff7ed; border:1px solid #fed7aa; color:#c2410c">
                    <i class="fas fa-clock text-orange-400"></i>
                    <span>
                      <strong>{{ form.societe.horaires.open_days.length }}</strong>
                      jour{{ form.societe.horaires.open_days.length > 1 ? 's' : '' }}/semaine —
                      de <strong>{{ form.societe.horaires.heure_ouverture }}</strong>
                      à <strong>{{ form.societe.horaires.heure_fermeture }}</strong>
                    </span>
                  </div>
                </transition>
              </div>


              <!-- ─── STEP 4 : ACTIVITÉS ─── -->
              <div v-else-if="currentStep.key === 'activites'">
                <div class="mb-6">
                  <h3 class="text-lg font-bold text-slate-900 mb-1">Activités de la société</h3>
                  <p class="text-sm text-slate-500">Sélectionnez les catégories d'appareils que vous prenez en charge.</p>
                </div>

                <!-- Loader -->
                <div v-if="activitesLoading" class="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
                  <i class="fas fa-spinner fa-spin"></i>
                  <span>Chargement des catégories…</span>
                </div>

                <!-- Grille catégories -->
                <div v-else class="grid grid-cols-2 gap-3">
                  <div
                    v-for="cat in categories" :key="cat.id"
                    @click="toggleCategorie(cat)"
                    class="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                    :class="cat.selected
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/40'"
                  >
                    <!-- Icône -->
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200"
                      :style="cat.selected ? 'background:#fff7ed' : 'background:#f1f5f9'">
                      <img v-if="cat.icon" :src="cat.icon" :alt="cat.label" class="w-5 h-5 object-contain" />
                      <i v-else class="fas fa-wrench text-slate-400" style="font-size:14px"></i>
                    </div>

                    <!-- Infos -->
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold truncate"
                        :class="cat.selected ? 'text-orange-700' : 'text-slate-700'">
                        {{ cat.label }}
                      </p>
                      <span v-if="cat.has_fluid"
                        class="inline-flex items-center gap-1 text-xs font-medium mt-0.5"
                        :class="cat.selected ? 'text-orange-500' : 'text-slate-400'">
                        <span class="w-1.5 h-1.5 rounded-full" :class="cat.selected ? 'bg-orange-400' : 'bg-slate-300'"></span>
                        Frigorigène
                      </span>
                    </div>

                    <!-- Checkbox -->
                    <div class="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200"
                      :style="cat.selected
                        ? 'background:#ea580c; border-color:#ea580c'
                        : 'border-color:#cbd5e1; background:white'">
                      <i v-if="cat.selected" class="fas fa-check text-white" style="font-size:8px"></i>
                    </div>
                  </div>
                </div>

                <!-- Résumé -->
                <transition name="fade">
                  <p v-if="categoriesSelectionnees.length > 0" class="mt-4 text-xs text-slate-500">
                    <i class="fas fa-circle-check text-emerald-500 mr-1.5"></i>
                    <strong class="text-slate-700">{{ categoriesSelectionnees.length }}</strong>
                    activité{{ categoriesSelectionnees.length > 1 ? 's' : '' }} sélectionnée{{ categoriesSelectionnees.length > 1 ? 's' : '' }}
                  </p>
                </transition>
              </div>


              <!-- ─── STEP 5 : ADMINISTRATEUR ─── -->
              <div v-else-if="currentStep.key === 'admin'" class="space-y-5">

                <div class="flex items-start gap-3 px-4 py-3.5 rounded-xl text-sm"
                  style="background:#eff6ff; border:1px solid #bfdbfe; color:#1d4ed8">
                  <i class="fas fa-wand-magic-sparkles flex-shrink-0 mt-0.5 text-blue-400"></i>
                  <span>Un lien de connexion magique sera envoyé par email — aucun mot de passe à définir.</span>
                </div>

                <!-- Info pré-remplissage -->
                <transition name="fade">
                  <div v-if="adminPrefilled"
                    class="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-xs font-semibold"
                    style="background:#fff7ed; border:1px solid #fed7aa; color:#c2410c">
                    <i class="fas fa-wand-magic-sparkles flex-shrink-0"></i>
                    <span>Pré-rempli depuis les informations du responsable — modifiez si besoin</span>
                  </div>
                </transition>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                      Prénom <span style="color:#ea580c">*</span>
                    </label>
                    <input v-model="form.admin.prenom" type="text" placeholder="Jean"
                      @blur="touch('admin_prenom')" @input="adminPrefilled = false"
                      class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200"
                      :class="fieldClass('admin_prenom', form.admin.prenom)"
                    />
                    <p v-if="touched.admin_prenom && errors.admin_prenom" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.admin_prenom }}</p>
                  </div>
                  <div>
                    <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                      Nom <span style="color:#ea580c">*</span>
                    </label>
                    <input v-model="form.admin.nom" type="text" placeholder="Dupont"
                      @blur="touch('admin_nom')" @input="adminPrefilled = false"
                      class="w-full border-2 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200"
                      :class="fieldClass('admin_nom', form.admin.nom)"
                    />
                    <p v-if="touched.admin_nom && errors.admin_nom" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.admin_nom }}</p>
                  </div>
                </div>

                <div>
                  <label class="block mb-1.5" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                    Email <span style="color:#ea580c">*</span>
                  </label>
                  <div class="relative">
                    <i class="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" style="font-size:11px"></i>
                    <input v-model="form.admin.email" type="email" placeholder="jean@masociete.fr"
                      @blur="touch('admin_email')" @input="adminPrefilled = false"
                      class="w-full border-2 rounded-xl py-3 text-sm outline-none transition-all duration-200"
                      style="padding-left:2.5rem; padding-right:2.5rem"
                      :class="fieldClass('admin_email', form.admin.email)"
                    />
                    <i v-if="touched.admin_email && !errors.admin_email"
                      class="fas fa-check-circle absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500 text-base"></i>
                  </div>
                  <p v-if="touched.admin_email && errors.admin_email" class="mt-1.5 ml-1 text-xs font-medium" style="color:#ef4444">{{ errors.admin_email }}</p>
                </div>
              </div>


              <!-- ─── STEP 6 : ABONNEMENT ─── -->
              <div v-else-if="currentStep.key === 'abonnement'" class="space-y-7">

                <div>
                  <label class="block mb-3" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                    Formule <span style="color:#ea580c">*</span>
                  </label>
                  <div class="grid grid-cols-2 gap-3">
                    <div
                      v-for="f in formules" :key="f.id"
                      @click="form.societe.abonnement.formule_abonnement_id = f.id"
                      class="p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 relative"
                      :class="form.societe.abonnement.formule_abonnement_id === f.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 hover:border-orange-200 bg-white'"
                    >
                      <div v-if="form.societe.abonnement.formule_abonnement_id === f.id"
                        class="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                        style="background:#ea580c">
                        <i class="fas fa-check text-white" style="font-size:8px"></i>
                      </div>
                      <p class="font-bold text-slate-900 text-base">{{ f.name }}</p>
                      <p class="mt-1 mb-3">
                        <span class="text-2xl font-bold" style="color:#ea580c">{{ f.price }}€</span>
                        <span class="text-xs text-slate-400 ml-1">/mois</span>
                      </p>
                      <p class="text-xs text-slate-500 leading-relaxed">{{ f.description }}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="block mb-3" style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#64748b">
                    Période d'essai
                    <span class="font-normal normal-case ml-1" style="letter-spacing:0; color:#cbd5e1; font-size:10px">optionnel</span>
                  </label>

                  <div class="flex flex-wrap gap-2 mb-3">
                    <button
                      v-for="preset in trialPresets" :key="preset.label"
                      @click="selectTrial(preset)"
                      class="px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-200"
                      :class="selectedTrialPreset === preset.label
                        ? 'border-orange-500 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600 bg-white'"
                      :style="selectedTrialPreset === preset.label ? 'background:#ea580c' : ''"
                    >
                      {{ preset.label }}
                    </button>
                    <button
                      @click="clearTrial"
                      class="px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-200"
                      :class="!selectedTrialPreset && !form.societe.abonnement.date_licence
                        ? 'border-slate-400 text-slate-700 bg-slate-100'
                        : 'border-slate-200 text-slate-400 hover:border-slate-300 bg-white'"
                    >
                      Aucune
                    </button>
                  </div>

                  <transition name="fade">
                    <div v-if="selectedTrialPreset === 'Personnalisé'" class="mt-2">
                      <input v-model="form.societe.abonnement.date_licence" type="date"
                        :min="today"
                        class="border-2 border-orange-300 rounded-xl px-4 py-2.5 text-sm outline-none transition-all focus:border-orange-500 bg-white"
                      />
                    </div>
                  </transition>

                  <transition name="fade">
                    <p v-if="form.societe.abonnement.date_licence && selectedTrialPreset !== 'Personnalisé'"
                      class="text-xs text-slate-500 mt-2 ml-1">
                      <i class="fas fa-calendar-check text-emerald-500 mr-1.5"></i>
                      Accès jusqu'au <strong class="text-slate-700">{{ formatDate(form.societe.abonnement.date_licence) }}</strong>
                    </p>
                  </transition>
                </div>
              </div>


            </div>
          </transition>

          <!-- ══ NAVIGATION ══ -->
          <div class="px-10 pb-8 flex items-center gap-3 border-t border-slate-100 pt-5">
            <button v-if="stepIndex > 0"
              @click="prevStep"
              class="flex items-center gap-2 px-4 h-11 rounded-xl border-2 border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-800 transition-all text-sm font-semibold flex-shrink-0 hover:bg-slate-50">
              <i class="fas fa-arrow-left text-xs"></i>
              Retour
            </button>

            <!-- Bouton "Passer" pour l'étape activités -->
            <button v-if="currentStep.key === 'activites'"
              @click="goTo(stepIndex + 1)"
              class="flex items-center gap-2 px-4 h-11 rounded-xl border-2 border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-800 transition-all text-sm font-semibold flex-shrink-0 hover:bg-slate-50">
              Passer
            </button>

            <button
              @click="handleNext"
              :disabled="submitting"
              class="flex-1 h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2.5 transition-all duration-300"
              :style="canGoNext && !submitting
                ? 'background:#ea580c; color:white; box-shadow:0 4px 16px rgba(234,88,12,0.35); cursor:pointer'
                : 'background:#f1f5f9; color:#94a3b8; cursor:not-allowed'"
            >
              <template v-if="submitting">
                <i class="fas fa-spinner fa-spin"></i>
                <span>Création en cours…</span>
              </template>
              <template v-else-if="currentStep.key === 'abonnement'">
                <i class="fas fa-rocket text-xs"></i>
                <span>Créer la société</span>
              </template>
              <template v-else-if="currentStep.key === 'activites'">
                <span>Continuer</span>
                <i class="fas fa-arrow-right text-xs"></i>
              </template>
              <template v-else>
                <span>Continuer</span>
                <i class="fas fa-arrow-right text-xs"></i>
              </template>
            </button>
          </div>

        </div>
      </div>
    </div>
    </div>
  </admin-template>
</template>


<script>
const GOUV_URL = 'https://api-adresse.data.gouv.fr/search/'

function fetchTimeout(url, ms = 4000) {
  const ctrl = new AbortController()
  const t    = setTimeout(() => ctrl.abort(), ms)
  return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(t))
}

export default {
  name: 'CreateSocieteWizard',

  data() {
    return {
      stepIndex:  0,
      direction:  1,
      submitting: false,

      // Pappers
      pappersLoading: false,
      pappersFilled:  false,
      pappersError:   null,

      autoFilled: {
        nom:                false,
        responsable_nom:    false,
        responsable_prenom: false,
        adresse:            false,
        code_postal:        false,
        ville:              false,
      },

      adminPrefilled: false,

      // Logo URL depuis input-file
      logoUrl: null,

      // Address autocomplete
      adresseSuggestions: [],
      adresseLoading:     false,
      adresseDebounce:    null,

      // Trial
      selectedTrialPreset: null,

      // Activités
      categories:      [],
      activitesLoading: false,

      // Succès
      showSuccess:      false,
      createdName:      '',
      createdActivites: 0,
      createdFormule:   '',

      touched: {},

      // ── META ──
      steps: [
        { key: 'siret',      label: 'SIRET',        sublabel: 'Identification'           },
        { key: 'societe',    label: 'Société',       sublabel: 'Infos & contact'         },
        { key: 'horaires',   label: 'Horaires',      sublabel: 'Jours et plages'         },
        { key: 'activites',  label: 'Activités',     sublabel: 'Types d\'appareils'      },
        { key: 'admin',      label: 'Administrateur',sublabel: 'Compte principal'        },
        { key: 'abonnement', label: 'Abonnement',    sublabel: 'Formule & période'       },
      ],

      jours: [
        { key: 'lundi',    short: 'Lun', full: 'Lundi'    },
        { key: 'mardi',    short: 'Mar', full: 'Mardi'    },
        { key: 'mercredi', short: 'Mer', full: 'Mercredi' },
        { key: 'jeudi',    short: 'Jeu', full: 'Jeudi'    },
        { key: 'vendredi', short: 'Ven', full: 'Vendredi' },
        { key: 'samedi',   short: 'Sam', full: 'Samedi'   },
        { key: 'dimanche', short: 'Dim', full: 'Dimanche' },
      ],

      formules: [
        { id: 1, name: 'Indépendant', price: 30, description: "L'arsenal complet pour l'artisan qui veut une structure de grand groupe." },
        { id: 2, name: 'Entreprise',  price: 55, description: "Pour piloter vos équipes, vos stocks et votre image de marque avec précision." },
      ],

      trialPresets: [
        { label: '1 semaine',    days: 7  },
        { label: '14 jours',     days: 14 },
        { label: '1 mois',       days: 30 },
        { label: 'Personnalisé', days: null },
      ],

      // ── FORM DATA ──
      form: {
        societe: {
          nom:                '',
          siret:              '',
          forme_juridique:    '',
          num_capacite:       '',
          adresse:            '',
          code_postal:        '',
          ville:              '',
          pays:               'France',
          latitude:           null,
          longitude:          null,
          tel:                '',
          mail:               '',
          responsable_nom:    '',
          responsable_prenom: '',
          url_google_avis:    '',
          horaires: {
            open_days:       ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
            heure_ouverture: '08:00',
            heure_fermeture: '18:00',
          },
          abonnement: {
            formule_abonnement_id: null,
            date_licence:          null,
          },
        },
        admin: {
          prenom: '',
          nom:    '',
          email:  '',
          color:  '#ea580c',
          theme:  'light',
          permissions: {
            can_invoice:      true,
            can_intervention: true,
            can_stats:        true,
            can_settings:     true,
            can_stock:        true,
            can_app_mobile:   true,
          },
        },
      },
    }
  },

  computed: {
    currentStep() { return this.steps[this.stepIndex] },
    today()       { return new Date().toISOString().split('T')[0] },

    categoriesSelectionnees() {
      return this.categories.filter(c => c.selected)
    },

    siretFieldClass() {
      if (this.pappersLoading) return 'border-orange-300 bg-orange-50 text-slate-900 focus:border-orange-400'
      if (this.pappersFilled)  return 'border-emerald-300 bg-emerald-50 text-slate-900'
      if (this.touched.siret && this.errors.siret) return 'border-red-300 bg-red-50 text-slate-900 focus:border-red-400'
      return 'border-slate-200 bg-white text-slate-900 focus:border-orange-400'
    },

    errors() {
      const s = this.form.societe
      const a = this.form.admin
      const e = {}

      // SIRET obligatoire
      if (!s.siret?.trim())          e.siret = 'Le SIRET est obligatoire.'
      else if (s.siret.length !== 14) e.siret = 'Le SIRET doit contenir exactement 14 chiffres.'

      if (!s.nom?.trim())               e.nom     = 'Le nom est requis.'
      else if (s.nom.trim().length < 2) e.nom     = 'Au moins 2 caractères.'
      if (!s.responsable_prenom?.trim() || s.responsable_prenom.trim().length < 2) e.responsable_prenom = 'Prénom requis (min. 2 caractères).'
      if (!s.responsable_nom?.trim()    || s.responsable_nom.trim().length < 2)    e.responsable_nom    = 'Nom requis (min. 2 caractères).'
      if (!s.adresse?.trim())  e.adresse = 'L\'adresse est requise.'
      if (!s.ville?.trim())    e.ville   = 'La ville est requise.'

      // Tel obligatoire
      if (!s.tel?.trim()) {
        e.tel = 'Le téléphone est requis.'
      } else {
        const d = s.tel.replace(/[\s.\-]/g, '')
        if (!/^(0[1-9]\d{8})$/.test(d) && !/^\+33[1-9]\d{8}$/.test(d))
          e.tel = 'Format invalide.'
      }

      // Mail obligatoire
      if (!s.mail?.trim()) {
        e.mail = 'L\'email est requis.'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.mail)) {
        e.mail = 'Email invalide.'
      }

      if (!s.horaires.heure_ouverture) e.heure_ouverture = 'Requis.'
      if (!s.horaires.heure_fermeture) {
        e.heure_fermeture = 'Requis.'
      } else if (s.horaires.heure_ouverture && s.horaires.heure_fermeture <= s.horaires.heure_ouverture) {
        e.heure_fermeture = 'Doit être après l\'ouverture.'
      }

      if (!a.prenom?.trim() || a.prenom.trim().length < 2) e.admin_prenom = 'Prénom requis (min. 2 caractères).'
      if (!a.nom?.trim()    || a.nom.trim().length < 2)    e.admin_nom    = 'Nom requis (min. 2 caractères).'
      if (!a.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) e.admin_email = 'Email valide requis.'

      if (!s.abonnement.formule_abonnement_id) e.formule = 'Choisissez une formule.'

      return e
    },

    canGoNext() {
      switch (this.currentStep.key) {
        case 'siret':      return !this.errors.siret
        case 'societe':    return !this.errors.nom && !this.errors.responsable_prenom && !this.errors.responsable_nom && !this.errors.adresse && !this.errors.ville && !this.errors.tel && !this.errors.mail
        case 'horaires':   return this.form.societe.horaires.open_days.length > 0 && !this.errors.heure_ouverture && !this.errors.heure_fermeture
        case 'activites':  return true
        case 'admin':      return !this.errors.admin_prenom && !this.errors.admin_nom && !this.errors.admin_email
        case 'abonnement': return !this.errors.formule
        default:           return true
      }
    },

    jsonPayload() {
      const s = this.form.societe
      return {
        societe: {
          nom:                s.nom,
          siret:              s.siret              || null,
          forme_juridique:    s.forme_juridique    || null,
          num_capacite:       s.num_capacite       || null,
          adresse:            s.adresse,
          code_postal:        s.code_postal,
          ville:              s.ville,
          pays:               s.pays,
          latitude:           s.latitude,
          longitude:          s.longitude,
          tel:                s.tel                || null,
          mail:               s.mail               || null,
          responsable_nom:    s.responsable_nom    || null,
          responsable_prenom: s.responsable_prenom || null,
          url_google_avis:    s.url_google_avis    || null,
          logo_url:           this.logoUrl         || null,
          horaires: {
            open_days:       s.horaires.open_days,
            heure_ouverture: s.horaires.heure_ouverture + ':00',
            heure_fermeture: s.horaires.heure_fermeture + ':00',
          },
          abonnement: {
            formule_abonnement_id: s.abonnement.formule_abonnement_id,
            date_licence:          s.abonnement.date_licence || null,
          },
        },
        compte_admin:   { ...this.form.admin },
        regles_metiers: { categories_appareils: this.categoriesSelectionnees.map(c => c.key) },
      }
    },
  },

  methods: {
    // ── Activités ──
    async loadCategories() {
      if (this.categories.length) return
      this.activitesLoading = true
      try {
        const response = await this.$request.post('categorie-appareil/all-categories')
        this.categories = (response || []).map(cat => ({ ...cat, selected: false }))
      } catch { /* ignore */ }
      finally { this.activitesLoading = false }
    },

    toggleCategorie(cat) {
      cat.selected = !cat.selected
    },

    // ── Navigation ──
    handleNext() {
      if (this.currentStep.key === 'abonnement') { this.submit(); return }
      this.touchCurrentStep()
      if (!this.canGoNext) return
      this.goTo(this.stepIndex + 1)
    },
    prevStep() { if (this.stepIndex > 0) this.goTo(this.stepIndex - 1) },
    goTo(idx) {
      this.direction = idx > this.stepIndex ? 1 : -1
      this.stepIndex = idx
      if (this.steps[idx]?.key === 'activites') this.loadCategories()
    },
    touchCurrentStep() {
      const map = {
        siret:      ['siret'],
        societe:    ['nom', 'responsable_prenom', 'responsable_nom', 'adresse', 'ville', 'tel', 'mail'],
        horaires:   ['open_days', 'heure_ouverture', 'heure_fermeture'],
        activites:  [],
        admin:      ['admin_prenom', 'admin_nom', 'admin_email'],
        abonnement: ['formule'],
      }
      ;(map[this.currentStep.key] || []).forEach(f => this.$set(this.touched, f, true))
    },
    touch(field) { this.$set(this.touched, field, true) },

    fieldClass(field, value, optional = false) {
      const err = this.touched[field] && this.errors[field]
      const ok  = this.touched[field] && !this.errors[field]
      if (err) return 'border-red-300 bg-red-50 text-slate-900 focus:border-red-400'
      if (ok)  return 'border-emerald-300 bg-white text-slate-900 focus:border-emerald-400'
      return 'border-slate-200 bg-slate-50 text-slate-900 focus:border-orange-400 focus:bg-white'
    },

    // ── SIRET → Pappers ──
    onSiretInput() {
      this.form.societe.siret = this.form.societe.siret.replace(/\D/g, '').slice(0, 14)
      this.pappersError       = null
      if (this.form.societe.siret.length === 14) {
        this.fetchFromPappers(this.form.societe.siret)
      } else {
        this.pappersFilled = false
      }
    },

    async fetchFromPappers(siret) {
      this.pappersLoading = true
      this.pappersFilled  = false
      this.pappersError   = null
      try {
        const response = await this.$request.get('/admin/pappers/autocomplete', { params: { siret } })

        if (!response?.found || !response?.data) {
          this.pappersError = 'Aucune société trouvée pour ce SIRET.'
          return
        }

        const d = response.data

        this._applyIfEmpty('nom',                d.nom_entreprise,      'nom')
        this._applyIfEmpty('responsable_prenom', d.responsable?.prenom, 'responsable_prenom')
        this._applyIfEmpty('responsable_nom',    d.responsable?.nom,    'responsable_nom')
        this._applyIfEmpty('adresse',            d.adresse,             'adresse')
        this._applyIfEmpty('code_postal',        d.code_postal,         'code_postal')
        this._applyIfEmpty('ville',              d.ville,               'ville')

        if (d.forme_juridique) this.form.societe.forme_juridique = d.forme_juridique
        if (d.latitude)        this.form.societe.latitude        = d.latitude
        if (d.longitude)       this.form.societe.longitude       = d.longitude

        if (this.form.societe.nom)     this.touch('nom')
        if (this.form.societe.adresse) this.touch('adresse')
        if (this.form.societe.ville)   this.touch('ville')

        // Pré-remplir l'admin depuis le responsable
        this.syncAdminFromResponsable()

        this.pappersFilled = true

        // Passage automatique à l'étape suivante
        setTimeout(() => {
          this.goTo(1)
        }, 800)

      } catch (err) {
        const status = err?.response?.status
        this.pappersError = status === 404 ? 'Aucune société trouvée.' : 'Service Pappers indisponible.'
      } finally {
        this.pappersLoading = false
      }
    },

    _applyIfEmpty(formField, value, autoKey) {
      if (!value) return
      if (!this.form.societe[formField]) {
        this.form.societe[formField] = value
        this.autoFilled[autoKey]     = true
      }
    },

    // ── Sync admin depuis responsable ──
    syncAdminFromResponsable() {
      // On met à jour l'admin tant que l'utilisateur n'a pas modifié manuellement
      if (this.form.societe.responsable_prenom && (this.adminPrefilled || !this.form.admin.prenom)) {
        this.form.admin.prenom = this.form.societe.responsable_prenom
        this.adminPrefilled = true
      }
      if (this.form.societe.responsable_nom && (this.adminPrefilled || !this.form.admin.nom)) {
        this.form.admin.nom = this.form.societe.responsable_nom
        this.adminPrefilled = true
      }
    },

    syncAdminMailFromSociete() {
      if (this.form.societe.mail && !this.form.admin.email) {
        this.form.admin.email = this.form.societe.mail
        this.adminPrefilled = true
      }
    },

    // ── Période d'essai ──
    selectTrial(preset) {
      this.selectedTrialPreset = preset.label
      if (preset.days !== null) {
        const d = new Date()
        d.setDate(d.getDate() + preset.days)
        this.form.societe.abonnement.date_licence = d.toISOString().split('T')[0]
      } else {
        this.form.societe.abonnement.date_licence = null
      }
    },
    clearTrial() {
      this.selectedTrialPreset                  = null
      this.form.societe.abonnement.date_licence = null
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const [y, m, d] = dateStr.split('-')
      return `${d}/${m}/${y}`
    },

    // ── Formatters ──
    formatTel() {
      const raw = this.form.societe.tel.replace(/\D/g, '').slice(0, 10)
      this.form.societe.tel = (raw.match(/.{1,2}/g) || []).join(' ')
    },
    toggleJour(key) {
      const idx = this.form.societe.horaires.open_days.indexOf(key)
      if (idx >= 0) this.form.societe.horaires.open_days.splice(idx, 1)
      else          this.form.societe.horaires.open_days.push(key)
    },

    // ── Address autocomplete ──
    onAdresseInput() {
      clearTimeout(this.adresseDebounce)
      if (this.form.societe.adresse.length < 3) { this.adresseSuggestions = []; return }
      this.adresseDebounce = setTimeout(() => this.adresseSearch(), 350)
    },
    async adresseSearch() {
      this.adresseLoading = true
      try {
        const res  = await fetchTimeout(`${GOUV_URL}?q=${encodeURIComponent(this.form.societe.adresse)}&limit=5`)
        const data = await res.json()
        this.adresseSuggestions = data.features.map(f => ({
          label:    f.properties.label,
          street:   f.properties.name,
          postcode: f.properties.postcode,
          city:     f.properties.city,
          coords:   f.geometry.coordinates,
        }))
      } catch { /* ignore */ }
      finally { this.adresseLoading = false }
    },
    adresseSelect(item) {
      this.form.societe.adresse     = item.street
      this.form.societe.code_postal = item.postcode
      this.form.societe.ville       = item.city
      this.form.societe.latitude    = item.coords[1]
      this.form.societe.longitude   = item.coords[0]
      this.adresseSuggestions = []
      this.autoFilled.adresse     = false
      this.autoFilled.code_postal = false
      this.autoFilled.ville       = false
      this.touch('adresse')
      this.touch('ville')
    },
    closeAdresse() { setTimeout(() => { this.adresseSuggestions = [] }, 150) },

    // ── Submit ──
    async submit() {
      this.submitting = true
      try {
        // Snapshot avant reset
        const nomSociete   = this.form.societe.nom
        const nbActivites  = this.categoriesSelectionnees.length
        const formuleId    = this.form.societe.abonnement.formule_abonnement_id
        const formuleLabel = (this.formules.find(f => f.id === formuleId) || {}).name || '—'

        await this.$request.post('/admin/societes/init', this.jsonPayload)

        // Stocker les infos pour l'écran de succès avant le reset
        this.createdName      = nomSociete
        this.createdActivites = nbActivites
        this.createdFormule   = formuleLabel

        this.showSuccess = true
        this.$nextTick(() => this.launchConfetti())

      } catch {
        if (this.$toast) this.$toast.error('Erreur lors de la création.')
      } finally {
        this.submitting = false
      }
    },

    resetAndClose() {
      this.showSuccess = false
      // Reset complet du formulaire
      this.stepIndex           = 0
      this.direction           = 1
      this.pappersFilled       = false
      this.pappersError        = null
      this.adminPrefilled      = false
      this.logoUrl             = null
      this.selectedTrialPreset = null
      this.categories          = []
      this.touched             = {}
      Object.keys(this.autoFilled).forEach(k => { this.autoFilled[k] = false })
      this.form.societe = {
        nom: '', siret: '', forme_juridique: '', num_capacite: '',
        adresse: '', code_postal: '', ville: '', pays: 'France',
        latitude: null, longitude: null, tel: '', mail: '',
        responsable_nom: '', responsable_prenom: '', url_google_avis: '',
        horaires: {
          open_days: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
          heure_ouverture: '08:00', heure_fermeture: '18:00',
        },
        abonnement: { formule_abonnement_id: null, date_licence: null },
      }
      this.form.admin = {
        prenom: '', nom: '', email: '', color: '#ea580c', theme: 'light',
        permissions: {
          can_invoice: true, can_intervention: true, can_stats: true,
          can_settings: true, can_stock: true, can_app_mobile: true,
        },
      }
    },

    launchConfetti() {
      const canvas = this.$refs.confettiCanvas
      if (!canvas) return
      canvas.width  = window.innerWidth
      canvas.height = window.innerHeight
      const ctx     = canvas.getContext('2d')
      const colors  = ['#f97316','#ea580c','#fbbf24','#34d399','#60a5fa','#a78bfa','#f472b6','#ffffff']
      const pieces  = Array.from({ length: 140 }, () => ({
        x:     Math.random() * canvas.width,
        y:     Math.random() * canvas.height - canvas.height,
        w:     Math.random() * 10 + 5,
        h:     Math.random() * 5 + 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        rot:   Math.random() * Math.PI * 2,
        vx:    (Math.random() - 0.5) * 3,
        vy:    Math.random() * 4 + 2,
        vr:    (Math.random() - 0.5) * 0.15,
        alpha: 1,
      }))

      let frame = 0
      const tick = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        let alive = false
        for (const p of pieces) {
          p.x   += p.vx
          p.y   += p.vy
          p.rot += p.vr
          p.vy  += 0.08
          if (frame > 120) p.alpha -= 0.012
          if (p.alpha <= 0) continue
          alive = true
          ctx.save()
          ctx.globalAlpha = p.alpha
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rot)
          ctx.fillStyle = p.color
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
          ctx.restore()
        }
        frame++
        if (alive && this.showSuccess) requestAnimationFrame(tick)
        else ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      requestAnimationFrame(tick)
    },

    // ── Transitions ──
    tBeforeEnter(el) {
      el.style.opacity   = '0'
      el.style.transform = `translateX(${this.direction * 40}px)`
    },
    tEnter(el, done) {
      el.offsetHeight
      el.style.transition = 'opacity 0.28s ease, transform 0.28s cubic-bezier(0.34,1.08,0.64,1)'
      el.style.opacity    = '1'
      el.style.transform  = 'translateX(0)'
      setTimeout(done, 280)
    },
    tLeave(el, done) {
      el.style.transition = 'opacity 0.16s ease, transform 0.16s ease-in'
      el.style.opacity    = '0'
      el.style.transform  = `translateX(${-this.direction * 40}px)`
      setTimeout(done, 160)
    },
  },
}
</script>

<style scoped>
/* ── Success screen transition ── */
.success-screen-enter-active { transition: opacity 0.35s ease; }
.success-screen-leave-active { transition: opacity 0.25s ease; }
.success-screen-enter-from,
.success-screen-leave-to    { opacity: 0; }

/* ── Success card pop-in ── */
.success-card {
  animation: cardPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes cardPop {
  from { opacity: 0; transform: scale(0.75) translateY(24px); }
  to   { opacity: 1; transform: scale(1)    translateY(0);    }
}

/* ── Ping rings ── */
@keyframes ping {
  75%, 100% { transform: scale(2); opacity: 0; }
}

/* ── Fade (used elsewhere) ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to       { opacity: 0; }

/* ── Dropdown ── */
.dropdown-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.dropdown-leave-active { transition: opacity 0.1s ease, transform 0.1s ease;  }
.dropdown-enter-from, .dropdown-leave-to { opacity: 0; transform: translateY(-4px); }
</style>